import { useField } from "formik";
import { useCallback, useMemo } from "react";
import Form from "react-bootstrap/esm/Form";
import ReactSelect, { MultiValue, SingleValue } from "react-select";
import { BaseInputProps, SelectOption, SelectOptionValue } from "../../../../models";
import { FieldError } from "../error-message";

export type SelectOptionType = MultiValue<SelectOption> | SingleValue<SelectOption>;

export interface SelectProps extends BaseInputProps {
    options: SelectOption[];
    defaultOption?: SelectOption;
    isClearable?: boolean;
    isMulti?: boolean;
    isSearchable?: boolean;
}

export const FormikSelect = (props: SelectProps) => {
    const { id, label, options, isMulti, disabled, placeholder, defaultOption } = props;
    const field = useField(props.name);
    const [{ name, value }, { error, touched }, { setValue }] = field;

    const styles = useMemo(
        () => ({
            option: (provided: any, state: any) => ({
                ...provided,
                borderBottom: "1px dotted gray",
                color: state.isSelected ? "white" : state.isDisabled ? "gray" : "blue",
            }),
            control: (provided: any) => ({
                ...provided,
                border: !!error && touched ? "1px solid red" : {},
            }),
            singleValue: (provided: any, state: any) => {
                const opacity = state.isDisabled ? 0.5 : 1;
                const transition = "opacity 300ms";
                return { ...provided, opacity, transition };
            },
        }),
        [error, touched]
    );

    const onChange = useCallback(
        (option: SelectOptionType) => {
            if (isMulti && Array.isArray(option)) {
                setValue(option.map((o: SelectOption) => o.value));
            } else {
                setValue((option as SelectOption)?.value);
            }
        },
        [isMulti, setValue]
    );

    const getValue = useCallback(() => {
        if (isMulti && Array.isArray(value)) {
            return options.filter((o) => value.some((v: SelectOptionValue) => v === o.value));
        } else {
            return options.find((o) => o.value === value) ?? null;
        }
    }, [value, isMulti, options]);

    return (
        <>
            <Form.Group controlId={id ?? name} className="mb-2">
                {label && <Form.Label>{label}</Form.Label>}
                <ReactSelect
                    value={getValue()}
                    defaultValue={defaultOption}
                    placeholder={placeholder}
                    onChange={onChange}
                    styles={styles}
                    options={options}
                    isMulti={isMulti}
                    isDisabled={disabled}
                />
                <FieldError name={name} />
            </Form.Group>
        </>
    );
};
